import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ProgressBarComponent } from '../progress-bar/progress-bar.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { 
  AbstractControl, 
  FormArray, 
  FormBuilder, 
  FormControl, 
  FormGroup, 
  FormsModule, 
  ReactiveFormsModule,
} from '@angular/forms';
import { BiqButtonComponent } from '../biq-button/biq-button.component';
import { BIQ_ETHNICITY, BIQ_TOTAL_QUESTIONS } from '@app/utils/constants';
import { MatFormFieldModule } from '@angular/material/form-field';
import { UserBiqData } from '@app/schemas/biq';
import { Store } from '@ngrx/store';
import { updateUserBiq } from '@app/state/actions/biq.action';
import { selfDescibeTextValidator } from '@app/validations/textValidations';

@Component({
  selector: 'app-biq-ethnicity',
  imports: [
    ProgressBarComponent,
    BiqButtonComponent,
    MatCheckboxModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
  ],
  templateUrl: './ethnicity.component.html',
  styleUrl: './ethnicity.component.css'
})
export class EthnicityComponent implements OnInit {
  @Output() continueEmitter = new EventEmitter<void>();
  public questNo: number = 4;
  public totalQuestions: number = BIQ_TOTAL_QUESTIONS;
  public ethnicityOptions = BIQ_ETHNICITY;
  public form: FormGroup;
  public selfDescribeControl = new FormControl(false);
  public selfDescribeText = new FormControl({ value: '', disabled: false });
  public storedBiqData!: UserBiqData;
  public disableOptions = ['Prefer not to answer', 'self-describe'];

  constructor(
    private fb: FormBuilder, 
    private store: Store<{ userBiq: UserBiqData }>,
    private cdr: ChangeDetectorRef,
  ) {
    this.store.select('userBiq').subscribe(value => {
      this.storedBiqData = value;
      if (value && value.ethnicity && value.ethnicity.length > 0 && this.form) {
        setTimeout(() => {
          this.restoreEthnicitySelections(value.ethnicity);
        }, 0);
      }
    });
    
    this.form = this.fb.group({
      ethnicities: new FormArray([], this.minSelectedCheckboxes(1))
    });

    this.form.addControl('customOption', this.selfDescribeControl);
    this.form.addControl('selfDescribeText', this.selfDescribeText);

    // Initialize the FormArray with controls (unchecked by default)
    this.ethnicityOptions.forEach(() => {
      (this.form.get('ethnicities') as FormArray).push(new FormControl(false));
    });

    // Re-evaluate form when user checks/unchecks self describe custom option
    this.selfDescribeControl.valueChanges.subscribe(() => {
      this.form.get('ethnicities')?.updateValueAndValidity();
    });
  }

  ngOnInit() {
    this.form.get('ethnicities')?.valueChanges.subscribe(() => {
      this.evaluateSelections();
    });
  
    this.selfDescribeControl.valueChanges.subscribe(() => {
      this.evaluateSelections();
    });

    if (this.storedBiqData?.ethnicity && this.storedBiqData.ethnicity.length > 0) {
      setTimeout(() => {
        this.restoreEthnicitySelections(this.storedBiqData.ethnicity);
      }, 0);
    }
  }

  evaluateSelections() {
    const formArray = this.form.get('ethnicities') as FormArray;
    const selectedValues = formArray.value as boolean[];
  
    const selectedIndexes = selectedValues
                            .map((checked, i) => checked ? i : -1)
                            .filter(i => i !== -1);
  
    const selectedOptionValues = selectedIndexes.map(i => this.ethnicityOptions[i].value);
    const isSelfDescribeChecked = this.selfDescribeControl.value === true;
  
    const selectedDisableOptionValue = [
      ...selectedOptionValues, 
      isSelfDescribeChecked ? 'self-describe' : ''
    ].find(value => this.disableOptions.includes(value));
  
    if (selectedDisableOptionValue) {
      selectedIndexes.forEach(i => {
        if (this.ethnicityOptions[i].value !== selectedDisableOptionValue) {
          formArray.at(i).setValue(false, { emitEvent: false });
        }
      });
  
      if (isSelfDescribeChecked && selectedDisableOptionValue !== 'self-describe') {
        this.selfDescribeControl.setValue(false, { emitEvent: false });
      }
  
      if (selectedDisableOptionValue !== 'self-describe') {
        this.selfDescribeText.setValue('', { emitEvent: false });
      }
    }

    if (isSelfDescribeChecked) {
      this.selfDescribeText.setValidators([selfDescibeTextValidator()]);
      this.selfDescribeText.enable({ emitEvent: false });
    } else {
      this.selfDescribeText.clearValidators();
      this.selfDescribeText.setValue('', { emitEvent: false });
      this.selfDescribeText.disable({ emitEvent: false });
    }
  
    this.selfDescribeText.updateValueAndValidity({ emitEvent: false });
  
    this.cdr.markForCheck?.();
  }

  shouldDisableCheckbox(index: number): boolean {
    const formArray = this.form.get('ethnicities') as FormArray;
    const selectedValues = formArray.value as boolean[];
    const selectedIndexes = selectedValues
                            .map((checked, i) => checked ? i : -1)
                            .filter(i => i !== -1);

    const selectedOptionValues = selectedIndexes.map(i => this.ethnicityOptions[i].value);
    const isSelfDescribeChecked = this.selfDescribeControl.value === true;
  
    const selectedDisableOptionValue = [
      ...selectedOptionValues, 
      isSelfDescribeChecked ? 'self-describe' : ''
    ].find(value => this.disableOptions.includes(value));

    return selectedDisableOptionValue
      ? this.ethnicityOptions[index].label !== selectedDisableOptionValue
      : false;
  }

  isSelfDescribeDisabled(): boolean {
    const formArray = this.form.get('ethnicities') as FormArray;
    const selectedValues = formArray.value as boolean[];
    const selectedOptionValues = selectedValues
                            .map((checked, i) => checked ? this.ethnicityOptions[i].value : '')
                            .filter(Boolean);
  
    const isSelfDescribeChecked = this.selfDescribeControl.value === true;
  
    const selectedDisableOptionLabel = [
      ...selectedOptionValues, 
      isSelfDescribeChecked ? 'self-describe' : ''
    ].find(value => this.disableOptions.includes(value));
  
    return selectedDisableOptionLabel
      ? selectedDisableOptionLabel !== 'self-describe'
      : false;
  }

  minSelectedCheckboxes(min = 1) {
    return (formArray: AbstractControl): { [key: string]: any } | null => {
      let totalSelected = (formArray as FormArray).controls
                            .map(control => control.value)
                            .filter(checked => checked).length;

      if (this.selfDescribeControl.value === true) {
        totalSelected += 1;
      }

      return totalSelected >= min ? null : { required: true };
    };
  }

  getSelectedOptions(): string[] {
    const arrayControl = this.form.get('ethnicities') as FormArray;
    const selected = this.ethnicityOptions
                      .filter((_, i) => arrayControl.at(i).value)
                      .map(option => option.value);
  
    if (this.selfDescribeControl.value && this.selfDescribeText.value?.trim()) {
      selected.push(this.selfDescribeText.value);
    }
  
    return selected;
  }

  private restoreEthnicitySelections(ethnicities: string[]) {
    const formArray = this.form.get('ethnicities') as FormArray;
    
    // Reset all checkboxes first
    formArray.controls.forEach(control => control.setValue(false, { emitEvent: false }));
    this.selfDescribeControl.setValue(false, { emitEvent: false });
    this.selfDescribeText.setValue('', { emitEvent: false });
    
    ethnicities.forEach(ethnicity => {
      const optionIndex = this.ethnicityOptions.findIndex(option => option.value === ethnicity);
      if (optionIndex >= 0) {
        formArray.at(optionIndex).setValue(true, { emitEvent: false });
      } else {
        this.selfDescribeControl.setValue(true, { emitEvent: false });
        this.selfDescribeText.setValue(ethnicity, { emitEvent: false });
      }
    });
  }

  onClickContinue() {
    const arrayControl = this.form.get('ethnicities') as FormArray;
    arrayControl.markAllAsTouched();
    if (this.form.invalid) return;

    const selectedEthnicity = this.getSelectedOptions();
    const updatedBiq = {
      ...this.storedBiqData,
      ethnicity: selectedEthnicity,
    }
    this.store.dispatch(updateUserBiq({ user: updatedBiq }));
    this.continueEmitter.emit();
  }
}
